import React, { useState, useEffect, useCallback, useContext, useRef } from 'react';
import { connect } from 'react-redux';
import { getBackend } from '../backend';
import {
  classifyDevice, listConfigCameras, readCamera, writeCamera, reconcileTypes, cameraSection, cameraFields, boolFromCfg, ptzDrivers,
} from './configParser';
import { SetupListPanel, SetupDrill, orgSubLabel, confirmDiscardIfDirty, useReportDirty, formsEqual, isMasterRole, resolveDisp, Field, SetupNavContext, useAutoSelectFirstInLandscape, isSetupMobilePortraitBase, setupPersistedSelection, saveSetupSelection, ssRemove, setupPersistedSubTab, saveSetupSubTab } from './setupCommon';
import { fieldAccess } from './setupAccess';   // the single source: '<menu>.<field>' → access; unknown → master-edit/local-readonly
import MotionGridEditor, { maskToCells, cellsToMask } from './MotionGridEditor';
import LprRoiEditor, { parseRoi, formatRoi } from './LprRoiEditor';

/**
 * CloudCameraSetup — cloud camera administration (unified-viewer-setup-design.md §4.2).
 *
 * Mirrors the Java dview camera frame (General / Video / PTZ / LPR — cohesive, not pixel-exact).
 * It edits the cloud-stored device config (JSDeviceConfig.sConfig, an INI blob) plus the friendly,
 * cloud-only camera name (JSCamera.sName). EDIT-ONLY — cloud cameras auto-register when the edge
 * device connects (no add), so the config's sections define the camera list.
 *
 * DATA-DRIVEN + capability-selected (David 2026-07-20): the family (dragon rich vs legacy thin) is
 * read from the config's OWN section naming, and each tab renders a control ONLY for fields the
 * device actually exposes (configParser.present) — so it adapts to a lagging/ahead edge build
 * without a hardcoded field list, and on save preserves every key it didn't touch.
 *
 * The BACKEND (/ve/camera, Phase-1 gated) is the authoritative gate; this UI only mirrors it.
 *
 * LAYOUT — shared mobile drill-down (SetupDrill): a device-grouped camera list; picking a camera
 * swaps to the tabbed detail with a ‹ Cameras back link + the camera name as heading. The motion /
 * LPR painter modals render as siblings of the shell (they're full-screen overlays).
 */

const TABS = [
  { id: 'general', label: 'General' },
  { id: 'video', label: 'Video' },
  { id: 'lpr', label: 'LPR' },
  { id: 'ptz', label: 'PTZ' },   // PTZ last — least-used tab
];

// Build the device-to-cameras tree from both parts of the /ve/cameras payload. Device rows keep a
// configured camera visible before JSCamera registers; camera rows supply the mirrored name and ID.
// Camera rows arrive once per profile, so dedupe them by (deviceId, bCamera).
const buildTree = (cameras, devices) => {
  const byDevice = {};
  const configOf = (row) => ((row && row.sConfig && typeof row.sConfig === 'object') ? row.sConfig : {});
  const hasStoredConfig = (config) => !!(config && typeof config === 'object' && Object.keys(config).length);
  for (const d of devices || []) {
    const id = d.sDeviceID;
    if (!id) continue;
    const config = configOf(d);
    const cls = classifyDevice(config);
    byDevice[id] = {
      deviceId: id,
      deviceName: d.sName || d.sDeviceName || id,
      customerName: d.sCustomerName || '',
      projectName: d.sProjectName || '',
      config,
      family: cls.family,
      cls,
      rows: {},
    };
  }
  for (const c of cameras || []) {
    const id = c.sDeviceID;
    if (!id) continue;
    if (!byDevice[id]) {
      const config = configOf(c);
      const cls = classifyDevice(config);
      byDevice[id] = {
        deviceId: id,
        deviceName: c.sDeviceName || id,
        customerName: c.sCustomerName || '',
        projectName: c.sProjectName || '',
        config,
        family: cls.family,
        cls,
        rows: {},           // bCamera -> { bCamera, sName, bID }
      };
    } else {
      const cameraConfig = configOf(c);
      if (!hasStoredConfig(byDevice[id].config) && hasStoredConfig(cameraConfig)) {
        const cls = classifyDevice(cameraConfig);
        byDevice[id] = { ...byDevice[id], config: cameraConfig, family: cls.family, cls };
      }
    }
    const dev = byDevice[id];
    if (dev.rows[c.bCamera] == null) {
      dev.rows[c.bCamera] = { bCamera: c.bCamera, sName: c.sName || '', bID: c.bID };
    }
  }
  // Any non-empty stored config decides which cameras exist. DB rows only provide names for those
  // configured cameras, so a stale JSCamera row cannot resurrect a removed camera. A truly empty config
  // falls back to registered rows until the first config arrives, and marks them read-only.
  return Object.values(byDevice).map((dev) => {
    const cfgCams = listConfigCameras(dev.config, dev.family);
    const rowCams = Object.keys(dev.rows).map(Number);
    const configured = hasStoredConfig(dev.config);
    const cameraNums = configured ? cfgCams : rowCams;
    // Each authorized slot carries its config-derived status (enabled=1) so the list can show
    // enabled/disabled at a glance — same truth as the Device General "N of M enabled" count.
    const cams = Array.from(new Set(cameraNums)).sort((a, b) => a - b)
      .map((n) => {
        const section = cameraSection(dev.config, n, dev.family);
        const isConfigured = configured && cfgCams.includes(n);
        const rowName = (dev.rows[n] && dev.rows[n].sName) || '';
        return {
          bCamera: n,
          sName: (isConfigured && section.name) || rowName || section.name || '',
          enabled: boolFromCfg(section.enabled),
          configured: isConfigured,
        };
      });
    return { ...dev, cams };
  }).sort((a, b) => (a.deviceName || '').toLowerCase()
    .localeCompare((b.deviceName || '').toLowerCase()));   // ordered by device name (unnamed → its ID)
};

// Canonicalize the mask / ROI strings to exactly what the editors emit, so an edit that returns the
// grid or region to its starting value compares byte-equal to the pristine snapshot (dirty clears).
// Only touches fields the device actually exposes.
const canonCam = (view) => ({
  ...view,
  ...(view.present.motionMask ? { motionMask: cellsToMask(maskToCells(view.motionMask)) } : {}),
  ...(view.present.lprRoi ? { lprRoi: formatRoi(parseRoi(view.lprRoi)) } : {}),
});

const CloudCameraSetup = (props) => {
  const [tree, setTree] = useState([]);
  const [configByDevice, setConfigByDevice] = useState({}); // deviceId -> parsed config (working, committed on save)
  const [selected, setSelected] = useState(null);           // { deviceId, bCamera, family }
  const [form, setForm] = useState(null);                   // readCamera() view (configName = editable config name)
  const [pristine, setPristine] = useState(null);           // canonicalized snapshot; dirty = form differs from it
  const [activeTab, setActiveTabRaw] = useState('general');
  const setActiveTab = useCallback((id) => { setActiveTabRaw(id); saveSetupSubTab('cameras', id); }, []);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [filter, setFilter] = useState('');
  const [expandedId, setExpandedId] = useState(null);       // which device is expanded (accordion; single-open)
  const [snapUrl, setSnapUrl] = useState('');               // recorded frame for the mask/ROI editors ('' = none)

  // Scoped entry: arrived from Device → "View cameras", so show ONLY that device's cameras and a
  // back link that returns to it (instead of the global camera list). Captured once at mount.
  const { go, params } = useContext(SetupNavContext);
  const [scope] = useState(() => (params && params.scopeDeviceId)
    ? { id: params.scopeDeviceId, name: params.scopeDeviceName || params.scopeDeviceId }
    : null);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const { cameras, devices } = await getBackend().getCameras({ token: props.sSess });
      const t = buildTree(cameras, devices);
      const cfg = {};
      for (const d of t) cfg[d.deviceId] = d.config;
      setTree(t); setConfigByDevice(cfg);
      // Expand the scoped device (else the first) so its cameras are visible on load.
      // Skip the default when a persisted camera selection exists — the restore effect
      // will expand the correct device. On mobile-portrait we IGNORE the persisted selection
      // (see the restore effect below) and land on the base list, so expand as if fresh. (#86bbfuajj 3P)
      if (isSetupMobilePortraitBase() || !setupPersistedSelection('cameras')) {
        setExpandedId(scope ? scope.id : (t.length ? t[0].deviceId : null));
      }

    } catch (e) {
      setError('Could not load cameras. ' + (e && e.message ? e.message : ''));
    } finally {
      setLoading(false);
    }
  }, [props.sSess, scope]);

  useEffect(() => { load(); }, [load]);

  // dirty is DERIVED: the live form differs from its canonicalized pristine snapshot.
  const dirty = form !== null && !formsEqual(form, pristine);
  const isMaster = isMasterRole(props.bRole);
  const configPending = !!(selected && selected.configured === false);
  const canManage = isMaster && !configPending;  // action-button gate; DB-only fallback rows stay read-only

  useReportDirty(dirty);

  const selSaveRef = useRef(false);
  useEffect(() => {
    if (!selSaveRef.current) { selSaveRef.current = true; return; }
    if (selected) {
      saveSetupSelection('cameras', { deviceId: selected.deviceId, bCamera: selected.bCamera });
    }
  }, [selected]);

  const camRestoredRef = useRef(false);
  useEffect(() => {
    if (camRestoredRef.current || !tree.length) return;
    const explicitFocus = params && params.focusCamera != null;
    // Mobile-portrait drill: land on the camera LIST, not the last-viewed camera's detail — no persistent
    // list column here, so restoring history is disorienting. A device-roster camera tap is explicit,
    // so honor that focus on every layout. Split tiers also keep normal history restore. (#86bbfuajj 3P)
    if (isSetupMobilePortraitBase() && !explicitFocus) return;
    const stored = explicitFocus
      ? { deviceId: scope && scope.id, bCamera: Number(params.focusCamera) }
      : setupPersistedSelection('cameras');
    if (!stored || typeof stored !== 'object' || !stored.deviceId) return;
    const device = tree.find((d) => d.deviceId === stored.deviceId);
    if (!device) { ssRemove('sel_cameras'); return; }
    const cam = (device.cams || []).find((c) => c.bCamera === stored.bCamera);
    if (!cam) { ssRemove('sel_cameras'); return; }
    camRestoredRef.current = true;
    const config = configByDevice[device.deviceId] || device.config;
    const view = readCamera(config, cam.bCamera, device.family);
    const f = canonCam({ ...view, configName: view.configName || cam.sName || '' });
    setSelected({ deviceId: device.deviceId, bCamera: cam.bCamera, family: device.family, configured: cam.configured !== false });
    setExpandedId(device.deviceId);
    setForm(f); setPristine(f);
    const restoredSubTab = setupPersistedSubTab('cameras');
    setActiveTabRaw(restoredSubTab || 'general');
    setError(''); setNotice('');
  }, [tree, configByDevice, params, scope]);

  const selectCamera = async (device, cam) => {
    if (!(await confirmDiscardIfDirty(dirty))) return;
    const config = configByDevice[device.deviceId] || device.config;
    const view = readCamera(config, cam.bCamera, device.family);
    // Config-authoritative name: the editable name is the config section's own `name`. Fall back to
    // JSCamera.sName for cameras named under the old model (their config `name` is still blank) — the
    // next save migrates that name into the config.
    const f = canonCam({ ...view, configName: view.configName || cam.sName || '' });
    setSelected({ deviceId: device.deviceId, bCamera: cam.bCamera, family: device.family, configured: cam.configured !== false });
    setExpandedId(device.deviceId);   // keep this device expanded when we return from the detail
    setForm(f); setPristine(f);
    setActiveTab('general'); setError(''); setNotice('');
  };

  // Back to the list; guard unsaved edits (this screen edits the device config, so it's careful).
  const closeDetail = async () => {
    if (!(await confirmDiscardIfDirty(dirty))) return;
    setSelected(null); setForm(null); setPristine(null); setActiveTab('general');
    setError(''); setNotice('');
  };

  const patch = (p) => { setForm((f) => ({ ...f, ...p })); setNotice(''); };

  const save = async () => {
    if (!selected || !form) return;
    const hasStreamField = !!(form.has && (form.has.recUrl || form.has.liveUrl));
    const hasStreamUrl = ['recUrl', 'liveUrl'].some((key) =>
      String(form[key] == null ? '' : form[key]).trim() !== '');
    if (hasStreamField && !hasStreamUrl) {
      setError('At least one stream URL is required.');
      return;
    }
    const invalidNumber = cameraFields(selected.family).find((field) => field.kind === 'num'
      && form.present[field.form]
      && (String(form[field.form] == null ? '' : form[field.form]).trim() === ''
        || !Number.isFinite(Number(form[field.form]))));
    if (invalidNumber) {
      const labels = {
        motionSens: 'Motion sensitivity', motionPost: 'Motion post', stillInterval: 'Still interval',
        lprMotionSens: 'Plate sensitivity', lprMotionPost: 'Plate post',
      };
      setError(`${labels[invalidNumber.form] || 'This field'} must be a number.`);
      return;
    }
    setBusy(true); setError(''); setNotice('');
    try {
      const baseConfig = configByDevice[selected.deviceId] || {};
      const fam = tree.find((d) => d.deviceId === selected.deviceId).family;
      const built = writeCamera(baseConfig, selected.bCamera, fam, form);
      // Type-echo against the loaded config so a numeric field with garbage/empty input never writes a
      // wrong-typed string — same device-safety net as the device save. (#86bbm58f7 Finding A)
      const newConfig = reconcileTypes(built, baseConfig);
      const { ok } = await getBackend().saveCamera({
        deviceId: selected.deviceId,
        bCamera: selected.bCamera,
        name: form.configName,   // mirror the config name → JSCamera.sName (peripheral surfaces read sName)
        config: newConfig,       // writeCamera has written form.configName into the [camN] name key
      }, { token: props.sSess });
      if (!ok) throw new Error('Save was rejected.');
      // Commit the working config + the friendly name into the tree, on success only.
      setConfigByDevice((m) => ({ ...m, [selected.deviceId]: newConfig }));
      setTree((t) => t.map((d) => d.deviceId !== selected.deviceId ? d : {
        ...d, config: newConfig,
        cams: d.cams.map((c) => c.bCamera === selected.bCamera
          ? { ...c, sName: form.configName, enabled: boolFromCfg(cameraSection(newConfig, c.bCamera, fam).enabled) }
          : c),
      }));
      setPristine(form);   // the saved form is the new pristine baseline
      setNotice('Camera saved.');
    } catch (e) {
      setError('Could not save. ' + (e && e.message ? e.message : 'You may not have permission.'));
    } finally { setBusy(false); }
  };

  // Undo unsaved edits in place — restore the pristine snapshot (dirty clears: form === pristine).
  const revert = () => { setForm(pristine); setError(''); setNotice(''); };

  // Frame for the mask/ROI editors: the most-recent RECORDED thumbnail for the selected camera
  // (dragon has no live single-frame JPEG; the field of view is fixed, so a recent frame shows the
  // right framing). Keyed on the camera id + generation-guarded (ignore flag) so a slow fetch for
  // one camera can't clobber the snapshot after switching cameras. '' → editors show "No snapshot".
  const selKey = selected ? `${selected.deviceId}:${selected.bCamera}` : '';
  useEffect(() => {
    let ignore = false;
    setSnapUrl('');
    if (!selKey) return undefined;
    getBackend().latestThumbUrl({ deviceId: selected.deviceId, cam: selected.bCamera, profile: 2 })
      .then((url) => { if (!ignore) setSnapUrl(url || ''); })
      .catch(() => { if (!ignore) setSnapUrl(''); });
    return () => { ignore = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selKey]);

  // A single-field/feature tab also hides when its primary field is access-hidden for this viewer, so
  // hiding the field hides its tab through the same one gate. useLpr is LPR's primary; ptz is PTZ's only
  // field. (#86bbm6j8w)
  const lprPrimary = form ? (form.present.useLpr ? 'camera.useLpr' : 'camera.lprRoi') : 'camera.useLpr';
  const showLprTab = form && (form.present.useLpr || form.present.lprRoi)
    && resolveDisp({ access: fieldAccess(lprPrimary) }, isMaster) !== 'hidden';
  const showPtzTab = form && form.present.ptz   // only when the edge config exposes a `ptz` driver field
    && resolveDisp({ access: fieldAccess('camera.ptz') }, isMaster) !== 'hidden';
  const tabs = TABS.filter((t) => (t.id !== 'lpr' || showLprTab) && (t.id !== 'ptz' || showPtzTab));

  // When scoped from a device, restrict the tree to that one device before filtering by search.
  const scopedTree = scope ? tree.filter((d) => d.deviceId === scope.id) : tree;
  const backToDevice = () => go('devices', { selectDeviceId: scope.id });

  const q = filter.toLowerCase();
  const filteredTree = scopedTree.map((d) => ({
    ...d,
    cams: d.cams.filter((c) =>
      !q ||
      (c.sName || '').toLowerCase().includes(q) ||
      `camera ${c.bCamera}`.includes(q) ||
      (d.deviceName || '').toLowerCase().includes(q) ||
      (d.customerName || '').toLowerCase().includes(q) ||
      (d.projectName || '').toLowerCase().includes(q)),
  })).filter((d) => d.cams.length);

  // Accordion: no filter → single device open (expandedId); with a filter → expand every match.
  const expandAll = filter.trim().length > 0;

  // mobile-landscape split → auto-select the first camera of the first device on first load so the
  // detail pane isn't empty (landscape-only; portrait keeps the list-first drill). selectCamera
  // expands that device too. filteredTree only holds devices that have cameras.
  useAutoSelectFirstInLandscape(filteredTree, selected !== null || setupPersistedSelection('cameras') != null, (dev) => selectCamera(dev, dev.cams[0]));

  // Stable heading — the SAVED camera name (from the tree's mirrored sName), not the live edit field.
  const detailTitle = (() => {
    if (!selected) return '';
    const dev = tree.find((d) => d.deviceId === selected.deviceId);
    const row = dev && dev.cams.find((c) => c.bCamera === selected.bCamera);
    return (row && row.sName) || `Camera ${selected.bCamera}`;
  })();

  return (
    <>
      <SetupDrill
        detailOpen={!!form}
        backLabel="Cameras"
        headingNav={form ? (
          <div className="setup-tabbar">
            {tabs.map((t) => (
              <button key={t.id}
                className={'setup-tabbar__tab' + (activeTab === t.id ? ' setup-tabbar__tab--active' : '')}
                onClick={() => setActiveTab(t.id)}>{t.label}</button>
            ))}
          </div>
        ) : null}
        title={detailTitle}
        onBack={closeDetail}
        list={(
          <>
            {scope ? (
              <button type="button" className="setup-cam-scope-back" onClick={backToDevice}>
                {'‹ ' + scope.name}
              </button>
            ) : null}
            <SetupListPanel
              searchFirst
              filter={filter}
              setFilter={setFilter}
              filterPlaceholder="Filter cameras…"
              loading={loading}
              empty={!filteredTree.length}
              emptyText="No cameras."
            >
            {filteredTree.map((d) => {
              const open = expandAll || expandedId === d.deviceId;
              const sub = orgSubLabel(d.customerName, d.projectName);
              return (
                <div key={d.deviceId} className="setup-device-group">
                  <div
                    className={'setup-device-group__hdr' + (open ? ' setup-device-group__hdr--open' : '')}
                    onClick={() => {
                      if (expandedId === d.deviceId) { setExpandedId(null); }
                      // Split tiers auto-open the first camera so the detail pane isn't empty; mobile-portrait
                      // is a drill — expanding a device reveals its camera LIST, not a jump into a cam. (#86bbfuajj 3P)
                      else { setExpandedId(d.deviceId); if (d.cams.length && !isSetupMobilePortraitBase()) selectCamera(d, d.cams[0]); }
                    }}
                  >
                    <span className="setup-device-group__chevron">{open ? '▼' : '▶'}</span>
                    <span className="setup-device-group__name">{d.deviceName}</span>
                    <span className={'setup-pill setup-pill--' + d.cls.pillClass}>{d.cls.pill}</span>
                    <span className="setup-device-group__count">
                      <span className="setup-device-group__count-n">{d.cams.length}</span>
                      <span className="setup-device-group__count-u">{d.cams.length === 1 ? 'cam' : 'cams'}</span>
                    </span>
                  </div>
                  {sub ? <div className="setup-device-group__site">{sub}</div> : null}
                  {open ? d.cams.map((c) => {
                    const isSel = selected && selected.deviceId === d.deviceId && selected.bCamera === c.bCamera;
                    return (
                      <div key={c.bCamera}
                        className={'setup-cam-row' + (isSel ? ' setup-cam-row--active' : '') + (c.enabled ? '' : ' setup-cam-row--off')}
                        onClick={() => selectCamera(d, c)}>
                        <span className="setup-cam-row__main">
                          <span className={'setup-cam-row__dot ' + (c.enabled ? 'setup-cam-row__dot--on' : 'setup-cam-row__dot--off')}
                            title={c.enabled ? 'Enabled' : 'Disabled'} />
                          <span className="setup-cam-row__name">{c.sName || `Camera ${c.bCamera}`}</span>
                        </span>
                        <span className="setup-cam-row__num">#{c.bCamera}</span>
                      </div>
                    );
                  }) : null}
                </div>
              );
            })}
            </SetupListPanel>
          </>
        )}
        actions={form && !configPending && (isMaster || dirty) ? (
          <>
            <div className="setup-actions__spacer" />
            {dirty ? (
              <button className="setup-btn--secondary" disabled={busy} onClick={revert}>Revert</button>
            ) : null}
            <button
              className={'setup-btn--save' + (dirty ? '' : ' setup-btn--save-clean')}
              disabled={busy || !dirty} onClick={save}>{busy ? 'Saving…' : 'Save'}</button>
          </>
        ) : null}
      >
        {form ? (
          <>
            <fieldset className="setup-fieldset" disabled={configPending}>
              <div className="setup-tabbar__body">
              {configPending ? <div className="setup-hint setup-hint--spaced">Camera settings are read-only until device config is available.</div> : null}
              {!configPending && !isMaster ? <div className="setup-hint setup-hint--spaced">Fields you can edit are enabled; the rest are read-only for your role.</div> : null}
              {/* Per-field access (#86bbm6j8w): every input is the shared <Field> (or a resolveDisp-gated
                  painter), so each camera field's master/local disposition is decided purely by data in
                  setupAccess — never a blanket role check here. */}
              {activeTab === 'general' && <GeneralTab form={form} patch={patch} isMaster={canManage} />}
              {activeTab === 'video' && <VideoTab form={form} patch={patch} snapshotUrl={snapUrl} isMaster={canManage} />}
              {activeTab === 'ptz' && showPtzTab && <PtzTab form={form} patch={patch} drivers={selected ? ptzDrivers(selected.family) : []} isMaster={canManage} />}
              {activeTab === 'lpr' && showLprTab && <LprTab form={form} patch={patch} snapshotUrl={snapUrl} isMaster={canManage} />}
              </div>
            </fieldset>

            {error ? <div className="setup-error">{error}</div> : null}
            {notice ? <div className="setup-notice">{notice}</div> : null}
          </>
        ) : null}
      </SetupDrill>
    </>
  );
};

// ---- tabs ------------------------------------------------------------------

const GeneralTab = ({ form, patch, isMaster }) => {
  // Every input is the shared <Field>, which resolves THIS viewer's disposition (edit / readonly /
  // hidden) from the field's own access in the single source (setupAccess) — the ONE gate, identical to
  // every other setup screen. To change what master or local may do to a field, edit setupAccess; never
  // touch this file. (#86bbm6j8w)
  // Use the loaded key-presence flags, not the current values. Clearing an editable URL must keep its
  // field mounted so the user can correct it or revert the form.
  const showRecUrl = !!(form.has && form.has.recUrl);
  const showLiveUrl = !!(form.has && form.has.liveUrl);
  const showJpgUrl = !!(form.has && form.has.jpgUrl);
  return (
  <div className="setup-facts setup-facts--2col">
    {/* Balanced 2-col facts grid: Camera Name + Stream ID on row 1, the two toggles on row 2, the long
        URLs span the full width below. (David 2026-08-24) */}
    <Field type="string" label="Camera Name" value={form.configName} access={fieldAccess('camera.name')} isMaster={isMaster}
      onChange={(v) => patch({ configName: v })} placeholder={`Camera ${form.cam}`} />
    {form.configName ? (
      <Field type="string" label="Stream ID" value={form.configName} access={fieldAccess('camera.streamId')} isMaster={isMaster} />
    ) : null}
    {/* Enabled + Pose: managed toggles through the SAME <Field> mechanism; Pose is additionally
        auto-disabled (a functional lock, layered on top of access) when the camera itself is disabled. */}
    {form.present.enabled ? (
      <Field type="bool" label="Enabled" value={form.enabled} access={fieldAccess('camera.enabled')} isMaster={isMaster}
        onChange={(v) => patch({ enabled: v })} />
    ) : null}
    {form.present.usePose ? (
      <Field type="bool" label="Pose detection" value={form.usePose} access={fieldAccess('camera.usePose')} isMaster={isMaster}
        disabled={!form.enabled} onChange={(v) => patch({ usePose: v })} />
    ) : null}
    {/* a divider (not a "Stream" label) separates the URLs; each URL label sits ABOVE its input so
        the long RTSP string gets the full width */}
    {(showRecUrl || showLiveUrl || showJpgUrl) ? <hr className="setup-hr" /> : null}
    {/* Record + Live URLs are master-editable by default (local read-only); the Snapshot URL is
        device-derived read-only. writeCamera persists an edit on save. NOTE: these RTSP URLs carry
        embedded credentials — shown here read-only before this change, so master-edit adds no new
        exposure. */}
    {showRecUrl ? (
      <Field type="string" label="Record URL" className="setup-row--stack" value={form.recUrl}
        access={fieldAccess('camera.recUrl')} isMaster={isMaster} onChange={(v) => patch({ recUrl: v })} />
    ) : null}
    {showLiveUrl ? (
      <Field type="string" label="Live URL" className="setup-row--stack" value={form.liveUrl}
        access={fieldAccess('camera.liveUrl')} isMaster={isMaster} onChange={(v) => patch({ liveUrl: v })} />
    ) : null}
    {showJpgUrl ? (
      <Field type="string" label="Snapshot URL" className="setup-row--stack" value={form.jpgUrl}
        access={fieldAccess('camera.snapshotUrl')} isMaster={isMaster} />
    ) : null}
  </div>
  );
};

const VideoTab = ({ form, patch, snapshotUrl, isMaster }) => {
  // Every input is the shared <Field> (which resolves master/local × edit/readonly/hidden from the
  // field's access in setupAccess). The Detection Area painter isn't a Field widget, so it reads the
  // SAME gate directly via resolveDisp(fieldAccess(...)) — hidden → not rendered, non-edit → disabled.
  // To change gating, edit setupAccess; never this file. (#86bbm6j8w)
  const [advOpen, setAdvOpen] = useState(false);
  const maskDisp = resolveDisp({ access: fieldAccess('camera.motionMask') }, isMaster);
  const hidden = (key) => resolveDisp({ access: fieldAccess('camera.' + key) }, isMaster) === 'hidden';
  const objectsOnly = !!form.objectOnly;
  const hasAny = form.present.motionMask || form.present.useOnnx || form.present.objectOnly
    || form.present.motionSens || form.present.motionPost || form.present.stillInterval
    || form.present.mpe || form.present.onvifUseTracker;
  return (
    <div className="setup-editor-2col setup-video-2col">
      {/* left: the controls; right: the Detection Area preview (Edit still opens the full-screen editor) */}
      <div className="setup-video-2col__main">
      <div className="setup-video-fields">
      {/* Record Mode leads: an either/or recording trigger (dragon `object_only`) as a segmented enum.
          "Objects only" implies object detection is on — selecting it also turns ONNX on. Motion
          mask/sensitivity stay relevant in BOTH modes (they gate WHEN the detector runs). */}
      {form.present.objectOnly ? (
        <Field type="enum" segmented label="Record Mode" value={objectsOnly ? 'objects' : 'motion'}
          access={fieldAccess('camera.objectOnly')} isMaster={isMaster}
          options={[{ value: 'motion', label: 'Motion' }, { value: 'objects', label: 'Objects only' }]}
          onChange={(v) => patch(v === 'objects' ? { objectOnly: true, useOnnx: true } : { objectOnly: false })} />
      ) : null}
      {form.present.useOnnx ? (
        <Field type="bool" label="Detect objects (ONNX)" value={form.useOnnx || objectsOnly}
          access={fieldAccess('camera.useOnnx')} isMaster={isMaster} disabled={objectsOnly}
          hint={objectsOnly ? 'Required for Objects only.' : undefined}
          onChange={(v) => patch({ useOnnx: v })} />
      ) : null}
      {form.present.motionSens ? (
        <Field type="number" label="Motion Threshold (%)" value={form.motionSens} step="any"
          access={fieldAccess('camera.motionSens')} isMaster={isMaster}
          hint="% of the detection area that must change to trigger. Lower = more sensitive."
          onChange={(v) => patch({ motionSens: v })} />
      ) : null}
      {form.present.motionPost ? (
        <Field type="number" label="Motion Post (s)" value={form.motionPost}
          access={fieldAccess('camera.motionPost')} isMaster={isMaster}
          hint="Seconds to keep recording after motion stops."
          onChange={(v) => patch({ motionPost: v })} />
      ) : null}
      {form.present.stillInterval ? (
        <Field type="number" label="Still Interval (min)" value={form.stillInterval}
          access={fieldAccess('camera.stillInterval')} isMaster={isMaster}
          onChange={(v) => patch({ stillInterval: v })} />
      ) : null}

      {!hasAny ? <div className="setup-muted">This camera exposes no video settings.</div> : null}
      </div>
      </div>
      {(form.present.motionMask || form.present.mpe || form.present.onvifUseTracker) ? (
        <div className="setup-video-2col__mask">
          {form.present.motionMask && maskDisp !== 'hidden' ? (
            <MotionGridEditor label="Detection Area" hint="Paint the areas where motion should trigger recording."
              value={form.motionMask} snapshotUrl={snapshotUrl} disabled={maskDisp !== 'edit'} onChange={(mask) => patch({ motionMask: mask })} />
          ) : null}
          {/* Advanced sits UNDER the Detection Area (uses the right column's spare height). It only shows
              when it has at least one field this viewer can actually see — for a local admin both mpe and
              onvifUseTracker are hidden, so the whole Advanced row is gone (revealed nothing). #86bbm6j8w */}
          {((form.present.mpe && !hidden('mpe')) || (form.present.onvifUseTracker && !hidden('onvifUseTracker'))) ? (
            <div className="setup-advanced">
              <button type="button" className="setup-advanced__toggle" aria-expanded={advOpen} onClick={() => setAdvOpen((o) => !o)}>
                <span className="setup-advanced__chevron">{advOpen ? '▼' : '▶'}</span> Advanced
              </button>
              {advOpen ? (
                <div className="setup-video-fields">
                  {form.present.mpe ? (
                    <Field type="bool" label="mpengine Events" value={form.mpe} access={fieldAccess('camera.mpe')} isMaster={isMaster}
                      hint="Send motion and object events to a linked mpengine NVR (POS overlay)."
                      onChange={(v) => patch({ mpe: v })} />
                  ) : null}
                  {form.present.onvifUseTracker ? (
                    <Field type="bool" label="ONVIF Tracking" value={form.onvifUseTracker} access={fieldAccess('camera.onvifUseTracker')} isMaster={isMaster}
                      hint="Give each object the camera reports over ONVIF a persistent ID and follow it across frames, instead of the raw per-frame detections."
                      onChange={(v) => patch({ onvifUseTracker: v })} />
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

// Cloud PTZ is a single setting: the pan/tilt/zoom driver protocol the edge device speaks
// (dragon/config.c + isite/engine/config.c `ptz=`). Presence of a recognized driver enables PTZ;
// "None" ('') disables it — there is no separate enable flag. (Named presets / live PTZ control are a
// separate, not-yet-built feature that belongs in the viewer, not in Setup.)
const PtzTab = ({ form, patch, drivers, isMaster }) => {
  const cur = form.ptz || '';
  // Preserve-unknown: if the stored driver is not in our list (a newer edge build), keep it selectable
  // so saving never clobbers it.
  const known = !cur || drivers.some((d) => d.value === cur);
  const opts = known ? drivers : [{ value: cur, label: cur + ' (current)' }, ...drivers];
  return (
    <div className="setup-facts">
      <Field type="enum" label="PTZ Protocol" value={cur} access={fieldAccess('camera.ptz')} isMaster={isMaster}
        options={[{ value: '', label: 'None (PTZ disabled)' }, ...opts.map((d) => ({ value: d.value, label: d.label }))]}
        hint="The pan/tilt/zoom protocol. Select None to turn PTZ off."
        onChange={(v) => patch({ ptz: v })} />
    </div>
  );
};

const LprTab = ({ form, patch, snapshotUrl, isMaster }) => {
  // The plate region + LPR tuning only matter when plate reading is on; lock them otherwise (a functional
  // lock layered on top of each field's own access gate). If the device doesn't expose useLpr, there's
  // nothing to gate on — leave them active.
  const lprOn = !form.present.useLpr || !!form.useLpr;
  // The Plate Region painter isn't a Field widget, so it reads the SAME gate directly via resolveDisp.
  const roiDisp = resolveDisp({ access: fieldAccess('camera.lprRoi') }, isMaster);
  // Mirror the Video tab: when a Plate Region editor is present, lay the controls (left) beside the
  // region preview (right) — a landscape-only grid (portrait stacks, unchanged).
  const hasRegion = !!form.present.lprRoi && roiDisp !== 'hidden';
  return (
    <div className={hasRegion ? 'setup-editor-2col' : undefined}>
      <div className={hasRegion ? 'setup-editor-2col__main' : undefined}>
        <div className="setup-video-fields">
        {/* The tab is already titled "LPR"; the enable toggle self-labels. */}
        {form.present.useLpr ? (
          <Field type="bool" label="Enable license plate recognition" value={form.useLpr}
            access={fieldAccess('camera.useLpr')} isMaster={isMaster} onChange={(v) => patch({ useLpr: v })} />
        ) : null}
        {form.present.lprMotionSens ? (
          <Field type="number" label="Plate Threshold (%)" value={form.lprMotionSens} step="any"
            access={fieldAccess('camera.lprMotionSens')} isMaster={isMaster} disabled={!lprOn}
            hint="% of the plate region that must change to trigger. Lower = more sensitive."
            onChange={(v) => patch({ lprMotionSens: v })} />
        ) : null}
        {form.present.lprMotionPost ? (
          <Field type="number" label="Plate Post (s)" value={form.lprMotionPost}
            access={fieldAccess('camera.lprMotionPost')} isMaster={isMaster} disabled={!lprOn}
            hint="Seconds to keep reading after motion stops."
            onChange={(v) => patch({ lprMotionPost: v })} />
        ) : null}
        </div>
      </div>
      {hasRegion ? (
        <div className="setup-editor-2col__mask">
          <LprRoiEditor label="Plate Region" hint="Draw the region where license plates are read." value={form.lprRoi} snapshotUrl={snapshotUrl} disabled={roiDisp !== 'edit' || !lprOn} onChange={(roi) => patch({ lprRoi: roi })} />
        </div>
      ) : null}
    </div>
  );
};

export { CloudCameraSetup }; // bare, for unit tests (pass sSess/bRole as props)

const mapStateToProps = (state) => ({
  sSess: state.auth.sSess,
  bRole: state.user.bRole,
});

export default connect(mapStateToProps)(CloudCameraSetup);
